import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import {
  getAnalyticsKpTimeline,
  type AnalyticsPeriodParams,
  type KpTimelineRow,
} from '../../shared/api/analytics.api'
import { Spinner } from '../../shared/ui/Spinner'
import { ErrorMessage } from '../../shared/ui/ErrorMessage'
import { STATUS_LABELS } from '../requests/components/requestsColDefs'
import type { RequestStatus } from '../../shared/api/types'
import { useAnalyticsTable, type ColumnDef } from './shared/useAnalyticsTable'
import { SortableTh } from './shared/SortableTh'

/**
 * Вкладка «КП» модуля «Аналитика» (для ADMIN/Гендиректора и РОП).
 *
 * Сводный быстрый экран: какие заявки на какой стадии у каждого менеджера.
 * По каждой заявке — таймлайн (создана → закупка просчитала всё → КП выставлено
 * → КП отправлено), следующее действие из блока «Контроль КП» + последнее
 * выполненное касание, статус и общий счётчик времени до финала. Клик по номеру
 * заявки открывает экран её истории (не стандартную карточку).
 */

const fmtDate = (iso: string | null): string =>
  iso ? new Date(iso).toLocaleDateString('ru-RU') : '—'

const statusLabel = (s: string): string => STATUS_LABELS[s as RequestStatus] ?? s

const ts = (iso: string | null): number => (iso ? Date.parse(iso) : 0)

/** Цвет счётчика времени: у активных дольше — тревожнее; финальные — нейтральны. */
const durationClass = (days: number, isFinal: boolean): string => {
  if (isFinal) return 'text-gray-500'
  if (days >= 30) return 'text-red-600 font-medium'
  if (days >= 14) return 'text-amber-600'
  return 'text-gray-700'
}

/**
 * Категория «Контроля КП» для фильтра. Временны́е категории вместо одной
 * «Открытое» (запрос Сергея 03.08): ставишь воронку «Сегодня» — видишь, по
 * каким заявкам сегодня нужен ответ, не проваливаясь в каждую. «Просрочено» —
 * бонусом для разбора хвостов.
 */
const controlCategory = (r: KpTimelineRow): string => {
  if (r.nextActionDueAt) {
    const due = new Date(r.nextActionDueAt)
    const dueDay = new Date(due.getFullYear(), due.getMonth(), due.getDate()).getTime()
    const now = new Date()
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime()
    if (dueDay === today) return 'Сегодня'
    return dueDay < today ? 'Просрочено' : 'Позже'
  }
  return r.lastDoneAt ? 'Выполнено' : '—'
}

// Фильтр-воронка на каждом столбце. У столбцов-дат значение фильтра — сама дата
// (либо «—» для пустой) → можно быстро отобрать заявки без нужной вехи, напр.
// «КП не отправлено» = фильтр «Отправлено» → «—».
const COLUMNS: ColumnDef<KpTimelineRow>[] = [
  {
    key: 'requestNumber',
    sort: (r) => r.requestNumber ?? '',
    filter: (r) => r.requestNumber ?? '—',
  },
  { key: 'managerName', sort: (r) => r.managerName.toLowerCase(), filter: (r) => r.managerName },
  { key: 'clientName', sort: (r) => r.clientName.toLowerCase(), filter: (r) => r.clientName },
  { key: 'createdAt', sort: (r) => ts(r.createdAt), filter: (r) => fmtDate(r.createdAt) },
  {
    key: 'fullyQuotedAt',
    sort: (r) => ts(r.fullyQuotedAt),
    filter: (r) => fmtDate(r.fullyQuotedAt),
  },
  { key: 'kpCreatedAt', sort: (r) => ts(r.kpCreatedAt), filter: (r) => fmtDate(r.kpCreatedAt) },
  { key: 'kpSentAt', sort: (r) => ts(r.kpSentAt), filter: (r) => fmtDate(r.kpSentAt) },
  { key: 'control', sort: (r) => ts(r.nextActionDueAt), filter: (r) => controlCategory(r) },
  { key: 'status', sort: (r) => statusLabel(r.status), filter: (r) => statusLabel(r.status) },
  { key: 'totalDays', sort: (r) => r.totalDays, filter: (r) => `${r.totalDays} дн` },
]

/** Начало локального дня даты — для сравнения «по дням», а не по моментам. */
const startOfDay = (d: Date): number =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()

/** Ячейка «Контроль КП»: открытое следующее действие или последнее выполненное. */
function ControlCell({ row }: { row: KpTimelineRow }) {
  if (row.nextActionDueAt) {
    // Сравнение по ДНЯМ (dueAt хранится полночью UTC — по моменту «сегодня»
    // горело бы красным почти весь день): красный — строго прошлые дни
    // (= воронка «Просрочено»), янтарный — сегодня (= «Сегодня», как dueTone
    // в карточке заявки), брендовый — позже.
    const dueDay = startOfDay(new Date(row.nextActionDueAt))
    const today = startOfDay(new Date())
    const tone =
      dueDay < today ? 'text-red-600' : dueDay === today ? 'text-amber-600' : 'text-brand-600'
    return (
      <div className="min-w-0" title={row.nextActionNote ?? undefined}>
        <span className={`font-medium ${tone}`}>→ {fmtDate(row.nextActionDueAt)}</span>
        {row.nextActionNote && (
          <div className="text-xs text-gray-400 truncate">{row.nextActionNote}</div>
        )}
      </div>
    )
  }
  if (row.lastDoneAt) {
    return (
      <div className="min-w-0" title={row.lastDoneNote ?? undefined}>
        <span className="text-gray-500">✓ {fmtDate(row.lastDoneAt)}</span>
        {row.lastDoneNote && (
          <div className="text-xs text-gray-400 truncate">{row.lastDoneNote}</div>
        )}
      </div>
    )
  }
  return <span className="text-gray-300">—</span>
}

export function KpTimelineTab({ periodParams }: { periodParams: AnalyticsPeriodParams }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['analytics-kp-timeline', periodParams],
    queryFn: () => getAnalyticsKpTimeline(periodParams),
  })

  const table = useAnalyticsTable(
    data?.rows ?? [],
    COLUMNS,
    { key: 'createdAt', dir: 'desc' },
    'kp-timeline',
  )

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner />
      </div>
    )
  }
  if (isError || !data) {
    return <ErrorMessage error="Не удалось загрузить КП-таймлайн" />
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <div className="flex items-baseline justify-between flex-wrap gap-2">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">КП-таймлайн заявок по менеджерам</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            Стадии заявки: создана → закупка просчитала все позиции → КП выставлено → КП отправлено
            заказчику. «Контроль КП» — следующее действие (→) или последнее выполненное касание (✓).
            «Время» — дней от создания до финала (или до сейчас). Клик по номеру — история заявки.
          </p>
        </div>
        <span className="text-xs text-gray-500">
          Заявок за период: <span className="font-semibold text-gray-900">{data.total}</span>
        </span>
      </div>

      {data.truncated && (
        <p className="mt-2 text-xs text-amber-600">
          Показаны первые {data.rows.length} из {data.total} — сузьте период для полного списка.
        </p>
      )}

      <div className="mt-3 max-h-[36rem] overflow-auto">
        <table className="w-full text-sm whitespace-nowrap">
          <thead>
            <tr className="text-left text-xs text-gray-500 border-b border-gray-200 [&_th]:sticky [&_th]:top-0 [&_th]:bg-white [&_th]:z-10">
              {/* Порядковый номер строки в текущем виде — чтобы счёт заявок
                  не путался с бейджем «×N КП по заявке» (запрос Сергея 03.08). */}
              <th className="py-2 pr-2 font-normal text-gray-400">№</th>
              <SortableTh table={table} col="requestNumber" className="pr-3">
                Заявка
              </SortableTh>
              <SortableTh table={table} col="managerName" className="px-3">
                Менеджер
              </SortableTh>
              <SortableTh table={table} col="clientName" className="px-3">
                Клиент
              </SortableTh>
              <SortableTh table={table} col="createdAt" className="px-3">
                Создана
              </SortableTh>
              <SortableTh table={table} col="fullyQuotedAt" className="px-3">
                Проквотирована
              </SortableTh>
              <SortableTh table={table} col="kpCreatedAt" className="px-3">
                КП выставлено
              </SortableTh>
              <SortableTh table={table} col="kpSentAt" className="px-3">
                Отправлено
              </SortableTh>
              <SortableTh table={table} col="control" className="px-3">
                Контроль КП
              </SortableTh>
              <SortableTh table={table} col="status" className="px-3">
                Статус
              </SortableTh>
              <SortableTh table={table} col="totalDays" align="right" className="pl-3">
                Время
              </SortableTh>
            </tr>
          </thead>
          <tbody>
            {table.view.map((r, idx) => (
              <tr key={r.requestId} className="border-b border-gray-50 hover:bg-gray-50/60">
                <td className="py-2 pr-2 text-xs text-gray-400">{idx + 1}</td>
                <td className="py-2 pr-3">
                  <Link
                    to={`/requests/${r.requestId}/history`}
                    className="text-brand-600 hover:text-brand-700 font-medium"
                  >
                    {r.requestNumber ?? '—'}
                  </Link>
                </td>
                <td className="py-2 px-3 text-gray-800">{r.managerName}</td>
                <td className="py-2 px-3 text-gray-700">{r.clientName}</td>
                <td className="py-2 px-3 text-gray-600">{fmtDate(r.createdAt)}</td>
                <td className={`py-2 px-3 ${r.fullyQuotedAt ? 'text-gray-600' : 'text-gray-300'}`}>
                  {fmtDate(r.fullyQuotedAt)}
                </td>
                <td className={`py-2 px-3 ${r.kpCreatedAt ? 'text-gray-600' : 'text-gray-300'}`}>
                  {fmtDate(r.kpCreatedAt)}
                  {r.kpCount > 1 && (
                    <span className="ml-1 text-xs text-gray-400">×{r.kpCount}</span>
                  )}
                </td>
                <td className={`py-2 px-3 ${r.kpSentAt ? 'text-gray-600' : 'text-gray-300'}`}>
                  {fmtDate(r.kpSentAt)}
                </td>
                <td className="py-2 px-3 max-w-[16rem]">
                  <ControlCell row={r} />
                </td>
                <td className="py-2 px-3 text-gray-600">{statusLabel(r.status)}</td>
                <td className={`py-2 pl-3 text-right ${durationClass(r.totalDays, r.isFinal)}`}>
                  {r.totalDays} дн{!r.isFinal && ' …'}
                </td>
              </tr>
            ))}
            {table.view.length === 0 && (
              <tr>
                <td colSpan={11} className="py-6 text-center text-sm text-gray-400">
                  Нет заявок за период
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
